import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { TIER_INFO, formatUSD } from '../lib/quoteEstimator';
import { AVAILABLE_HOSTING_PRICE_MAX, HOSTING_PRICE_MIN } from '../data/hostingPlans';
import PageHeader from '../components/ui/PageHeader';
import Tabs from '../components/ui/Tabs';
import Accordion, { type AccordionItem } from '../components/ui/Accordion';

const a = 'text-primary-300 hover:text-primary-200 underline underline-offset-4';

const Examples: React.FC<{ rows: string[][] }> = ({ rows }) => (
  <ul className="mt-3 grid grid-cols-1 min-[420px]:grid-cols-3 gap-2">
    {rows.map((row) => (
      <li key={row.join('|')} className="rounded-xl border border-secondary-800 bg-secondary-900/60 px-4 py-3 sm:px-5 text-center">
        {row.map((cell, j) => (
          <p key={cell} className={`text-xs ${j === 2 ? 'text-white font-bold' : 'text-secondary-300'}`}>
            {cell}
          </p>
        ))}
      </li>
    ))}
  </ul>
);

const ADDONS: AccordionItem[] = [
  {
    id: 'standard',
    title: 'Standard content-management interface',
    meta: '0.75×',
    content: (
      <>
        <p className="text-xs font-semibold text-primary-300 mb-1">Approved · available from T1 up where the interface only touches public content</p>
        <p>
          Lets staff edit public website content — text, menus, prices, images, announcements — without new commissions. The fee is 75% of the original development commission (combined ×1.75).
          Applies even on T4/T5 when the panel only touches public content. Administering orders or third-party account data triggers an assessment instead.
        </p>
        <Examples
          rows={[
            ['$500 build', '+$375 panel', '$875 combined'],
            ['$1,500 build', '+$1,125 panel', '$2,625 combined'],
            ['$8,000 build', '+$6,000 panel', '$14,000 combined'],
          ]}
        />
      </>
    ),
  },
  {
    id: 'advanced',
    title: 'Advanced application-management interface',
    meta: '1.00×+',
    content: (
      <>
        <p className="text-xs font-semibold text-primary-300 mb-1">Approved minimum · T4+ only</p>
        <p>
          For privileged backend work: stored records, accounts, permissions, moderation, transactions. Starts at 100% of base with no maximum — intensity sets the multiplier. A T4A panel needing persistent records triggers T4B scope review.
        </p>
        <Examples
          rows={[
            ['$5,000 build', '+$5,000 min', '$10,000 combined min'],
            ['$8,000 build', '+$8,000 min', '$16,000 combined min'],
            ['$18,000 build', '+$18,000 min', '$36,000 combined min'],
          ]}
        />
        <p className="mt-2 text-xs text-secondary-400 italic">Upgrade credits, rebaselining after rebuilds, and panel-modification pricing are still open questions.</p>
      </>
    ),
  },
  {
    id: 'ai',
    title: 'AI self-healing',
    meta: 'Engineering quote',
    content: (
      <>
        <p className="text-xs font-semibold text-primary-300 mb-1">
          T4+ with FairCorp hosting · not a self-serve price ·{' '}
          <span className="rounded-full border border-accent-400/40 bg-accent-500/10 px-2 py-0.5 text-[11px] font-bold text-accent-200">ENGINEERING QUOTE</span>
        </p>
        <p>
          If you request AI self-healing, the build includes it in full as a completion requisite. The price is an official engineering quote. It is not an automatic add-on on T1–T3.
        </p>
      </>
    ),
  },
  {
    id: 'hosting',
    title: 'Hosting and the risk allowance',
    meta: 'separate contract',
    content: (
      <p>
        Hosting is optional and a separate contract from development: 17 approved plans. H1–H4 are available now from {formatUSD(HOSTING_PRICE_MIN)}–{formatUSD(AVAILABLE_HOSTING_PRICE_MAX)} per month; H5 hosting is not available at this time. The{' '}
        <Link to="/plans" className={a}>hosting plans page</Link> has every plan, the finder, and the illustrative F-hosting risk allowance (not approved policy). T3A/T3B are development tiers; H3a/H3b are hosting configurations — matching letters imply nothing.
      </p>
    ),
  },
];

const POLICIES: AccordionItem[] = [
  {
    id: 'independent',
    title: 'Independent contracts',
    content: <p>Development and hosting are separate purchases. Host elsewhere, bring an external build to us, or leave hosting without forfeiting your site.</p>,
  },
  {
    id: 'minor',
    title: 'Complimentary minor changes',
    content: <p>Small text, content, hours, image, and styling fixes are free at every tier — no hosting purchase required. Major new work is quoted individually (T4+ mods from ~$100).</p>,
  },
  {
    id: 'own',
    title: 'You own your work',
    content: <p>Commissioned source code and accumulated data are yours to transfer. Sensitive exports travel by hand-delivered physical media.</p>,
  },
  {
    id: 'safety',
    title: '6-month safety net',
    content: <p>Eligible recovery backups are retained up to six months after hosting ends — a transfer window, not extended hosting.</p>,
  },
];

const TierPanel: React.FC<{ tier: (typeof TIER_INFO)[number] }> = ({ tier }) => (
  <div className="panel p-4 sm:p-5">
    <h2 className="text-xl sm:text-2xl font-bold text-white">{tier.name}</h2>
    <p className="mt-1 text-sm text-primary-200">{tier.tagline}</p>
    <p className="mt-3 text-sm text-secondary-300">{tier.description}</p>
    <p className="mt-4 flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
      <span className="text-2xl font-bold text-white">{tier.priceDisplay}</span>
      <span className="text-xs text-secondary-400">{tier.priceNote}</span>
    </p>
    <p className="text-sm text-secondary-300 mt-1">Hosting: {tier.hostingRange}</p>
    <ul className="mt-4 grid gap-2 sm:grid-cols-2">
      {tier.features.map((f) => (
        <li key={f} className="text-sm text-secondary-200 bg-secondary-900/70 border border-secondary-800 rounded-xl px-3 py-2">
          {f}
        </li>
      ))}
    </ul>
    <p className="mt-4 text-sm text-secondary-300">
      <span className="font-semibold text-white">Best for: </span>
      {tier.bestFor}
    </p>
    <div className="mt-4 flex flex-col sm:flex-row gap-2">
      <Link to={`/tiers/${tier.tier}`} className="btn btn-outline">
        Full {tier.tier} details <ArrowRight className="w-4 h-4" aria-hidden="true" />
      </Link>
      <Link to="/pricing?view=calculator" className="btn btn-quiet">
        Estimate a {tier.tier} project
      </Link>
    </div>
  </div>
);

const Tiers: React.FC = () => (
  <div className="page-inner pb-10">
    <PageHeader
      kicker={<span className="chip-dev">Development · one-time</span>}
      title="Tier reference"
      lead="Seven development classifications, the optional add-ons, and the standing policies. Preview only — final quotes are staff-reviewed."
      actions={
        <Link to="/pricing?view=calculator" className="btn btn-primary">
          Open the pricing calculator <ArrowRight className="w-4 h-4" aria-hidden="true" />
        </Link>
      }
    />

    <Tabs
      label="Reference sections"
      tabs={[
        {
          id: 'tiers',
          label: 'Development tiers',
          panel: (
            <div className="space-y-3">
              <p className="text-sm text-secondary-300">
                Tier 3 splits into <strong className="text-white">3A (we build the experience)</strong> and <strong className="text-white">3B (we connect existing services)</strong> at the same approved price; a project needing both is one Tier 3 commission. T3A/T3B are development tiers, not the H3a/H3b hosting configurations.
              </p>
              <Tabs
                label="Tier selector"
                size="sm"
                defaultValue="T3A"
                tabs={TIER_INFO.map((t) => ({
                  id: t.tier,
                  label: t.tier,
                  ariaLabel: `Show ${t.tier}`,
                  panel: <TierPanel tier={t} />,
                }))}
              />
            </div>
          ),
        },
        { id: 'addons', label: 'Add-ons', panel: <Accordion level={2} items={ADDONS} defaultOpen={['standard']} /> },
        { id: 'policy', label: 'Policy', panel: <Accordion level={2} items={POLICIES} multiple defaultOpen={['independent']} /> },
      ]}
    />
  </div>
);

export default Tiers;
